import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import { acknowledgeableFrom, canAcknowledge } from '@kraftwerk/shared';
import { db } from '../db/local.js';
import { acknowledgeIntake, listIntakesForDay, skipIntake } from '../db/intakes.js';
import type { DueIntake } from '../db/intakes.js';
import { useTimeZone } from '../hooks/useToday.js';

/**
 * Was heute ansteht — und der Haken, um den es in diesem Projekt geht.
 *
 * Liest aus der lokalen Datenbank und schreibt dorthin zurück. Quittieren
 * funktioniert deshalb im Funkloch; der Server erfährt es beim nächsten Abgleich
 * und hört dann auf nachzufassen.
 *
 * **Eine Einnahme steht vorn, der Rest darunter.** Der Griff zum Telefon gilt
 * fast immer der nächsten fälligen — die bekommt die Kernzahl und zwei
 * Daumenflächen, alles andere eine Zeile. Die Liste bleibt vollständig: wer
 * gestern Abend etwas übersprungen hat, soll das heute noch sehen können.
 */
export function TodayReminders({
  highlightIntakeId = null,
}: {
  /** Aus einer Benachrichtigung angesteuerte Einnahme — wird hervorgehoben. */
  highlightIntakeId?: string | null;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const timeZone = useTimeZone();
  const now = useNow();
  // Die Zeitzone wird hineingereicht, nicht in der Abfrage nachgeschlagen —
  // sonst verliert Dexie die Beobachtung (siehe CLAUDE.md).
  const intakes = useLiveQuery(() => listIntakesForDay(new Date(), db, timeZone), [timeZone], []);

  const zeit = new Intl.DateTimeFormat(i18n.language, { hour: '2-digit', minute: '2-digit' });
  /** Statt der Knöpfe: ab wann abgehakt werden kann — oder dass es gleich so weit ist. */
  const notYet = (entry: DueIntake) =>
    entry.preview && canAcknowledge(entry.dueAt, now)
      ? t('today.preparing')
      : t('today.ackFrom', { time: zeit.format(acknowledgeableFrom(entry.dueAt)) });
  const alle = intakes ?? [];
  const offen = alle.filter((entry) => !entry.acknowledgedAt && !entry.skippedAt);

  // Die angetippte Einnahme steht vorn, solange sie offen ist — wer aus einer
  // Benachrichtigung kommt, meint genau sie und nicht die nächste nach Uhrzeit.
  const angetippt = offen.find((entry) => entry.id === highlightIntakeId);
  const naechste = angetippt ?? offen[0];
  const danach = offen.find((entry) => entry.id !== naechste?.id);

  // Was vorn steht, steht nicht noch einmal darunter. Zweimal „Genommen" für
  // dieselbe Einnahme wären zwei Knöpfe, von denen einer überflüssig ist — und
  // man sähe ihm nicht an, welcher.
  const uebrige = alle.filter((entry) => entry.id !== naechste?.id);
  // Der Rest des Tages ist zugeklappt: „Heute" zeigt das Nächste, nicht den
  // ganzen Tag zweimal. Aufgeklappt wird von selbst, wenn eine Mitteilung auf
  // eine Einnahme zeigt, die nicht vorn steht — sonst sähe man sie nicht.
  const [aufgeklappt, setAufgeklappt] = useState(false);
  const restId = useId();
  const zeigeRest = aufgeklappt || uebrige.some((entry) => entry.id === highlightIntakeId);

  return (
    <section aria-labelledby="heute-heading" className="flex flex-col gap-3">
      <h2 id="heute-heading" className="sr-only">
        {t('today.heading')}
      </h2>

      {naechste ? (
        <div className="kw-hero">
          <p className="kw-kicker">
            {t('today.next')} · {t('today.openCount', { count: offen.length })}
          </p>
          <p className="kw-figure mt-2 text-6xl">{zeit.format(naechste.dueAt)}</p>
          <p className="mt-1.5 text-base font-semibold">
            {naechste.supplementName}
            {naechste.dose ? ` · ${naechste.dose}` : ''}
          </p>
          {danach ? (
            <p className="mt-0.5 text-sm text-ink-muted">
              {t('today.after', {
                name: danach.supplementName,
                time: zeit.format(danach.dueAt),
              })}
            </p>
          ) : null}

          {!ready(naechste, now) ? (
            <p className="mt-4 text-sm text-ink-muted">{notYet(naechste)}</p>
          ) : (
            <div className="mt-4 flex gap-2.5">
              <button
                type="button"
                onClick={() => void acknowledgeIntake(naechste.id)}
                aria-label={t('today.takeNamed', { name: naechste.supplementName })}
                className="kw-btn flex-1"
              >
                {t('today.take')}
              </button>
              <button
                type="button"
                onClick={() => void skipIntake(naechste.id, null)}
                aria-label={t('today.skipNamed', { name: naechste.supplementName })}
                // 46 % statt 40 %: bei 375 px bricht „Heute nicht" sonst im Knopf um.
                className="kw-btn kw-btn-quiet basis-[46%] whitespace-nowrap"
              >
                {t('today.skip')}
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="kw-hero">
          <p className="kw-kicker">{t('today.heading')}</p>
          <p className="mt-2 text-base">
            {alle.length > 0 ? t('today.nothingOpen') : t('today.empty')}
          </p>
        </div>
      )}

      {uebrige.length > 0 ? (
        <>
          <button
            type="button"
            aria-expanded={zeigeRest}
            aria-controls={zeigeRest ? restId : undefined}
            onClick={() => setAufgeklappt(!zeigeRest)}
            className="kw-btn kw-btn-quiet kw-btn-small"
          >
            {zeigeRest ? t('today.hideRest') : t('today.moreToday', { count: uebrige.length })}
          </button>
          {zeigeRest ? (
            <ul id={restId} className="flex flex-col gap-2">
              {uebrige.map((entry) => (
                <IntakeRow
                  key={entry.id}
                  intake={entry}
                  time={zeit.format(entry.dueAt)}
                  highlighted={entry.id === highlightIntakeId}
                  ready={ready(entry, now)}
                  notYet={notYet(entry)}
                />
              ))}
            </ul>
          ) : null}
        </>
      ) : null}
    </section>
  );
}

function IntakeRow({
  intake,
  time,
  highlighted,
  ready: abhakbar,
  notYet,
}: {
  intake: DueIntake;
  time: string;
  highlighted: boolean;
  /** Knöpfe erst ab 15 Minuten vorher und nur für echte (geplante) Einnahmen. */
  ready: boolean;
  notYet: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  const erledigt = intake.acknowledgedAt !== null;
  const uebersprungen = intake.skippedAt !== null;

  return (
    <li
      ref={(node) => {
        // `scrollIntoView` gibt es nicht überall (jsdom, ältere Umgebungen). Ein
        // fehlendes Scrollen darf die Zeile nicht verschwinden lassen.
        if (highlighted && typeof node?.scrollIntoView === 'function') {
          node.scrollIntoView({ block: 'center' });
        }
      }}
      // Umbrechen statt quetschen: Bei zwei Knöpfen bleibt auf 375 px sonst so
      // wenig für den Namen, dass „Vitamin B12" als „Vita…" dasteht. Ein
      // abgeschnittener Name ist schlechter als eine zweite Zeile.
      className={`kw-tile flex flex-wrap items-center gap-x-3 gap-y-2.5 ${
        highlighted ? 'border-accent' : ''
      } ${erledigt || uebersprungen ? 'opacity-70' : ''}`}
    >
      <div className="flex min-w-0 flex-1 basis-1/2 items-baseline gap-2.5">
        <span className="kw-figure shrink-0 text-lg">{time}</span>
        <span className="min-w-0">
          <span className="block truncate text-sm">{intake.supplementName}</span>
          {intake.dose ? (
            <span className="block truncate text-xs text-ink-muted">{intake.dose}</span>
          ) : null}
        </span>
      </div>

      {erledigt ? (
        <span className="kw-chip ml-auto shrink-0 border-good text-good">{t('today.done')}</span>
      ) : uebersprungen ? (
        <span className="kw-chip ml-auto shrink-0 border-warn text-warn">{t('today.skipped')}</span>
      ) : !abhakbar ? (
        <span className="ml-auto shrink-0 text-xs text-ink-muted">{notYet}</span>
      ) : (
        <div className="ml-auto flex shrink-0 gap-2">
          <button
            type="button"
            onClick={() => void acknowledgeIntake(intake.id)}
            aria-label={t('today.takeNamed', { name: intake.supplementName })}
            className="kw-btn kw-btn-small px-3"
          >
            {t('today.take')}
          </button>
          <button
            type="button"
            onClick={() => void skipIntake(intake.id, null)}
            aria-label={t('today.skipNamed', { name: intake.supplementName })}
            className="kw-btn kw-btn-quiet kw-btn-small px-3"
          >
            {t('today.skip')}
          </button>
        </div>
      )}
    </li>
  );
}

/** Abhaken geht ab 15 Minuten vorher — und nur, wenn der Server die Einnahme schon geplant hat. */
function ready(entry: DueIntake, now: Date): boolean {
  return !entry.preview && canAcknowledge(entry.dueAt, now);
}

/** Die Uhr für das Abhak-Fenster: tickt halbminütlich, damit der Knopf pünktlich erscheint. */
function useNow(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(timer);
  }, []);
  return now;
}
